
export enum SubscriptionStatus {
  ACTIVE = 'Active',
  PAST_DUE = 'Past Due',
  CANCELED = 'Canceled',
  TRIAL = 'Trial',
  PAUSED = 'Paused'
}

export enum PlanTier {
  FREE = 'Free',
  STARTER = 'Starter',
  PRO = 'Pro',
  ENTERPRISE = 'Enterprise'
}

export enum DiscountType {
  PERCENTAGE = 'Percentage',
  FIXED = 'Fixed Amount'
}

export type RiskLevel = 'Low' | 'Medium' | 'High';
export type AdminRole = 'Owner' | 'Admin' | 'Developer' | 'Support';
export type WorkspaceRole = 'Admin' | 'Editor' | 'Viewer';
export type AppEnvironment = 'sandbox' | 'production';
export type LanguageCode = 'en' | 'es' | 'de' | 'fr' | 'hi';

export interface MeteredRule {
  id: string;
  unitName: string;
  pricePerUnit: number;
  startAtUnit: number;
}

export interface ActivityEvent {
  id: string;
  type: 'signup' | 'payment_success' | 'payment_failed' | 'subscription_canceled' | 'upgrade' | 'downgrade' | 'dunning_triggered';
  customerName: string;
  value?: number;
  currency?: string;
  timestamp: string;
}

export interface RetentionOffer {
  id: string;
  reasonMatch: string; // matches churn_reason
  offerType: 'discount' | 'credit' | 'pause' | 'snooze';
  value: number;
  durationMonths?: number;
  isEnabled: boolean;
}

export interface BulkUpdateJob {
  id: string;
  name: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  processedCount: number;
  totalCount: number;
  createdAt: string;
}

export interface PortalBranding {
  name: string;
  logoUrl: string;
  footerText: string;
}

export interface AbandonedCart {
  id: string;
  email: string;
  customerName?: string;
  planTier: string;
  planPrice: number;
  currency: string;
  abandonedAt: string;
  status: 'Captured' | 'Recovering' | 'Recovered' | 'Lost';
  recoveryEmailSent: boolean;
  lastStep?: string;
}

export interface WorkspaceTierConfig {
  includedWorkspaces: number; // e.g., 1 workspace included by default
  additionalWorkspaceUnitPrice: number; // e.g., $15 per additional workspace / mo
  maxWorkspacesAllowed?: number; // Optional limit, e.g., 20
}

export interface WorkspaceAddonAssignment {
  addonId: string;
  name: string;
  price: number;
  currency: string;
  billingCycle?: 'Monthly' | 'Annually';
  assignedAt: string;
}

export interface CustomerWorkspace {
  id: string;
  name: string;
  slug: string;
  customerId: string;
  subscriptionId?: string;
  isPrimary: boolean;
  createdAt: string;
  activeAddons: WorkspaceAddonAssignment[];
  memberCount?: number;
  environment?: 'production' | 'staging';
}

export interface CheckoutAddon {
  id: string;
  name: string;
  price: number;
  currency?: string;
  description: string;
  features?: string[];
  isEnabled: boolean;
  isPerWorkspace?: boolean; // If true, must be purchased and provisioned per specific workspace
}

export interface TestimonialConfig {
  quote: string;
  author: string;
  role: string;
  avatarUrl: string;
}

export interface RecoveryConfig {
  isEnabled: boolean;
  automatedSequence: boolean;
  delayHours: number;
  abandonmentThresholdMin: number;
  templateId: string;
}

// Ensure all missing types from components are present
export interface Customer {
  id: string;
  name: string;
  email: string;
  phone?: string;
  signupDate: string;
  totalLtv: number;
  location: string;
  gstin?: string;
  riskLevel: RiskLevel;
  lastActive: string;
  credits: CreditWallet;
  billingAddress?: string;
  poNumber?: string;
}

export interface CreditWallet {
  balance: number;
  used: number;
  totalAllocated: number;
  velocity?: CreditVelocity;
}

export interface CreditVelocity {
  currentDailyAverage: number;
  estimatedZeroDate: string;
  burnRateTrend: 'increasing' | 'stable' | 'decreasing';
}

export interface Subscription {
  id: string;
  gatewayRef: string;
  customerId: string;
  customerName: string;
  plan: PlanTier;
  status: SubscriptionStatus;
  mrr: number;
  billingCycle: 'Monthly' | 'Annually';
  startDate: string;
  nextBillingDate: string;
  autoRenew: boolean;
  pausedUntil?: string;
  purchasedWorkspaces?: number;
  workspaceAddonAmount?: number;
  scheduledChange?: {
    targetPlan: PlanTier;
    effectiveDate: string;
    action: 'upgrade' | 'downgrade';
  };
}

export interface Invoice {
  id: string;
  invoiceNumber: string;
  customerId: string;
  customerName: string;
  date: string;
  amount: number;
  currency: string;
  status: 'Paid' | 'Open' | 'Void' | 'Uncollectible';
  planTier: PlanTier;
  pdfUrl: string;
}

export interface PlanDetail {
  id: string;
  tier: string;
  price: number;
  currency: string;
  description: string;
  isPopular?: boolean;
  color: string;
  features: string[];
  aiCredits: number;
  isActive: boolean;
  bulkDiscountRules?: BulkDiscountRule[];
  meteredRules?: MeteredRule[];
  addons?: CheckoutAddon[];
  workspaceTierConfig?: WorkspaceTierConfig;
  quarterlyDiscountPercentage?: number;
  yearlyDiscountPercentage?: number;
}

export interface BulkDiscountRule {
  threshold: number;
  value: number;
  type: DiscountType;
}

export interface Coupon {
  id: string;
  code: string;
  discountType: DiscountType;
  value: number;
  currency: string;
  startDate: string;
  expiryDate: string | null;
  minPurchase: number | null;
  limitPerCustomer: boolean;
  maxRedemptions: number | null;
  currentRedemptions: number;
  status: 'Active' | 'Scheduled' | 'Expired';
  redemptions?: CouponRedemption[];
}

export interface CouponRedemption {
  customerName: string;
  redeemedAt: string;
  savedAmount: number;
}

export interface WebhookEndpoint {
  id: string;
  url: string;
  description: string;
  events: string[];
  isEnabled: boolean;
  createdAt: string;
}

export interface AIInsight {
  title: string;
  description: string;
  impact: 'High' | 'Medium' | 'Low';
  action: string;
}

export interface MetricData {
  mrr: number;
  arr: number;
  churnRate: number;
  activeUsers: number;
  revenueGrowth: number;
  recoveredRevenue: number;
  arpu?: number;
  nrr?: number;
  totalCustomers?: number;
  activeSubscriptions?: number;
  revenue?: number;
  grossMargin?: number;
  momGrowth?: number;
  targetArr?: number;
}

export interface MRRMovement {
  month: string;
  new: number;
  expansion: number;
  contraction: number;
  churn: number;
}

export interface ChartData {
  month: string;
  revenue: number;
  users: number;
  forecast?: number;
}

export interface ActiveSession {
  id: string;
  device: string;
  location: string;
  ip: string;
  isCurrent: boolean;
  lastActive: string;
}

export interface AuditLog {
  id: string;
  adminName: string;
  action: string;
  target: string;
  timestamp: string;
  ip: string;
}

export interface AnalyticsConfig {
  googleAnalyticsId: string;
  isGoogleAnalyticsEnabled: boolean;
  facebookPixelId: string;
  isFacebookPixelEnabled: boolean;
  clarityProjectId: string;
  isClarityEnabled: boolean;
  trackPageViews: boolean;
  trackPurchases: boolean;
  trackCheckouts: boolean;
}

export interface AppSettings {
  environment: AppEnvironment;
  isGstEnabled: boolean;
  gstRate: number;
  businessGstin: string;
  gatewayRules: GatewayRule[];
  gatewayCredentials: GatewayCredentials;
  dunningRules: DunningRule[];
  cancellationSaverRules: CancellationSaverRule[];
  retentionOffers: RetentionOffer[];
  emailTemplates: EmailTemplate[];
  developerSettings: DeveloperSettings;
  checkoutConfig: CheckoutConfig;
  portalBranding: PortalBranding;
  isAIConsultantEnabled: boolean;
  analyticsConfig?: AnalyticsConfig;
}

export interface GatewayRule {
  id: string;
  gateway: PaymentGatewayId;
  currencies: string[];
  countries: string[];
  isEnabled: boolean;
}

export type PaymentGatewayId = 'stripe' | 'razorpay' | 'cashfree' | 'lemonsqueezy' | 'paddle' | 'checkoutcom' | 'chargebee';

export interface GatewayCredentials {
  stripe: { publishableKey: string; secretKey: string; webhookSecret: string };
  razorpay: { keyId: string; keySecret: string; webhookSecret: string };
  cashfree: { appId: string; secretKey: string; environment: 'sandbox' | 'production' };
  lemonsqueezy: { apiKey: string; webhookSecret: string };
  paddle: { vendorId: string; authCode: string; publicKey: string };
  checkoutcom: { publicKey: string; secretKey: string; channelId: string };
  chargebee: { site: string; publishableKey: string };
}

export interface DunningRule {
  id: string;
  name: string;
  isEnabled: boolean;
  gracePeriodDays: number;
  maxDunningDays: number;
  steps: DunningStep[];
}

export interface CancellationSaverRule {
  id: string;
  name: string;
  isEnabled: boolean;
  offerType: 'discount' | 'credit' | 'pause' | 'snooze';
  discountValue?: number;
  creditAmount?: number;
  pauseMonths?: number;
  snoozeDays?: number;
}

export interface DunningStep {
  id: string;
  dayDelay: number;
  emailTemplateId: string;
  action: 'retry' | 'mark_past_due' | 'cancel';
}

export interface EmailTemplate {
  id: string;
  name: string;
  category: 'Billing' | 'Recovery' | 'Lifecycle' | 'Marketing';
  subject: string;
  body: string;
  lastModified: string;
}

export interface DeveloperSettings {
  publicKey: string;
  secretKey: string;
  webhookSecret: string;
  webhooks: WebhookEndpoint[];
}

export interface CheckoutABTest {
  id: string;
  name: string;
  description: string;
  isEnabled: boolean;
  variations: {
    id: string;
    name: string;
    config: Partial<CheckoutConfig>;
    trafficWeight: number; // 0-100
    stats?: {
      views: number;
      conversions: number;
    };
  }[];
}

export interface CheckoutConfig {
  template: CheckoutTemplateId;
  logoUrl: string;
  paymentDisplayName?: string;
  primaryColor: string;
  secondaryColor: string;
  backgroundColor: string;
  textColor: string;
  fontFamily: string;
  buttonText: string;
  buttonRadius: string;
  buttonShadow: string;
  inputStyle: 'outline' | 'solid';
  showSecurityBadge: boolean;
  allowCoupons: boolean;
  couponPlaceholder: string;
  couponDiscountPercentage: number;
  headerMain: string;
  headerSub: string;
  showSocialProof: boolean;
  socialProofText: string;
  showFomo: boolean;
  fomoMessage: string;
  showTestimonial: boolean;
  testimonial: TestimonialConfig;
  showTrustBar: boolean;
  trustLogos: string[];
  showGuarantee: boolean;
  guaranteeText: string;
  termsUrl: string;
  footerText: string;
  quarterlyDiscountPercentage: number;
  yearlyDiscountPercentage: number;
  recoveryConfig: RecoveryConfig;
  addons: CheckoutAddon[];
  useMultiStep: boolean;
  showTaxBreakdown: boolean;
  enableQuantity: boolean;
  quantityLabel: string;
  maxQuantity: number;
  customFields: CheckoutField[];
  abTests: CheckoutABTest[];
}

export type CheckoutTemplateId = 'saas' | 'classic' | 'minimal' | 'glass' | 'cyberpunk' | 'bento' | 'split_dark' | 'floating' | 'vertical' | 'neumorphic' | 'feature_rich' | 'mono' | 'playful' | 'clean_stripe' | 'social_heavy';

export interface CheckoutField {
  id: string;
  label: string;
  placeholder: string;
  type: string;
  required: boolean;
  isEnabled: boolean;
}

export interface TeamMember {
  id: string;
  name: string;
  email: string;
  role: AdminRole;
  avatar: string;
  lastActive: string;
}

export interface WorkspaceMember {
  id: string;
  name: string;
  email: string;
  role: WorkspaceRole;
  avatar: string;
  joinedAt: string;
}

export interface CustomerApiToken {
  id: string;
  name: string;
  tokenPrefix: string;
  createdAt: string;
  lastUsed: string;
}

export interface UsageLogEntry {
  id: string;
  timestamp: string;
  action: string;
  resource: string;
  cost: number;
}

export interface UnbilledCharge {
  id: string;
  description: string;
  amount: number;
  date: string;
}

export interface PaymentMethod {
  id: string;
  brand: string;
  last4: string;
  expiry: string;
  isDefault: boolean;
  nickname?: string;
}

export interface WebhookEvent {
  id: string;
  label: string;
  desc: string;
}

export type ChurnReason = 'too_expensive' | 'missing_features' | 'buggy' | 'switching' | 'other';

export interface RevenueAnomaly {
  id: string;
  type: 'churn_spike' | 'usage_drop' | 'expansion_opportunity';
  severity: 'Critical' | 'Warning' | 'Insight';
  title: string;
  description: string;
  impactValue: number;
  detectedAt: string;
}

export interface GSTR1Entry {
  gstin: string;
  receiverName: string;
  invoiceNumber: string;
  invoiceDate: string;
  invoiceValue: number;
  placeOfSupply: string;
  reverseCharge: string;
  invoiceType: string;
  taxRate: number;
  taxableValue: number;
  cessAmount: number;
}

export interface CohortData {
  cohort: string;
  size: number;
  retention: number[];
}

export interface TrackedEventDefinition {
  id: string;
  name: string;
  description: string;
  eventCode: string;
  isCustom: boolean;
  createdAt: string;
}

export interface TrackedEventLog {
  id: string;
  customerId: string;
  eventName: string;
  timestamp: string;
  metadata: Record<string, any>;
}

export interface AIUsageLog {
  id: string;
  timestamp: string;
  customerId: string;
  customerName: string;
  model: string;
  task: string;
  creditsConsumed: number;
  status: string;
}
